import './dateRangePickerStyles.css'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { DateRangePicker, CustomProvider } from 'rsuite'
import ptBR from 'rsuite/locales/pt_BR'
const { afterToday } = DateRangePicker
import {
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from '@mui/material'
import { Search } from '@mui/icons-material'
import {
  CustomTableCell,
  Filter,
  GroupedFilterTags,
  TableHead,
} from '../../components'
import { getComparator } from '../../utils/graph'
import filterDrawerHelper from '../../utils/filterDrawer'
import { STATUS } from '../../utils/notificationStatus'
import useViewport from '../../hooks/useViewport'
import { useTranslation } from "react-i18next"

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin: 16px 0;
`

const SearchContainer = styled.div`
  display: flex;
  flex: 2;
  justify-content: flex-start;
  align-items: center;
  border: 1px solid #d6d7db;
  border-radius: 4px;
  padding: 4px 8px;
  height: 40px;
  gap: 8px;
`

const StyledInput = styled.input`
  border: 0;
  width: 100%;
  outline: none;
`

const StickyTableCell = styled(TableCell)`
  min-width: 50px;
  left: 0;
  position: sticky;
  z-index: 2;
  background-color: #fff;
`

const StyledDateRangePicker = styled(DateRangePicker)`
  height: 20px;
`

const rowsPerPage = 10

const ContentTab = ({
  page,
  onChangePage,
  loadingData,
  rows,
  inputValue,
  handleType,
  dateRange,
  handleDateRange,
  selectedFilterOptions,
  setOpenFilterDrawer,
  updateAnnotation,
}) => {
  const { t } = useTranslation('common')

  const translateSeverities = {
    low: t('annotations.tab.low'),
    medium: t('annotations.tab.medium'),
    high: t('annotations.tab.high')
  }

  const headState = [
    {
      id: 'employee',
      numeric: false,
      disablePadding: true,
      label: t('annotations.tab.employee'),
      align: 'left',
      sticky: true,
    },
    {
      id: 'message',
      numeric: false,
      disablePadding: false,
      label: t('annotations.tab.message'),
      align: 'center',
    },
    {
      id: 'date',
      numeric: false,
      disablePadding: false,
      label: t('annotations.tab.date'),
      align: 'center',
    },
    {
      id: 'severity',
      numeric: false,
      disablePadding: false,
      label: t('annotations.tab.severity'),
      align: 'center',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: t('annotations.tab.status'),
      align: 'center',
    },
  ]

  const [order, setOrder] = useState('desc')
  const { width } = useViewport()
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper
  const [orderBy, setOrderBy] = useState(headState[2].id)

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (_, newPage) => {
    onChangePage(newPage)
  }

  const emptyRows = () => {
    return page > 0 || rows.length < 10
      ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
      : 0
  }

  const visibleRows = useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows],
  )

  const onStatusChange = (newStatus) => {
    updateAnnotation({
      id: newStatus.id,
      update_type: 'status',
      value: newStatus.status
    })
  }

  const onSeverityChange = (newSeverity) => {
    updateAnnotation({
      id: newSeverity.id,
      update_type: 'severity',
      value: newSeverity.severity
    })
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FiltersContainer>
          <SearchContainer>
            <Search sx={{ color: '#858B92' }} />
            <StyledInput
              placeholder={t('annotations.tab.search_placeholder')}
              value={inputValue}
              onChange={handleType}
            />
          </SearchContainer>
          <div style={{ display: 'flex', gap: 8 }}>
            <CustomProvider locale={ptBR}>
              <StyledDateRangePicker
                defaultCalendarValue={dateRange}
                value={dateRange}
                onChange={value => handleDateRange({ value })}
                showOneCalendar
                shouldDisableDate={afterToday()}
                character=' - '
                placement='bottom'
                placeholder={t('annotations.tab.date_placeholder')}
                ranges={[]}
              />
            </CustomProvider>
            <Filter onClick={() => setOpenFilterDrawer(true)} />
          </div>
        </FiltersContainer>
        {selectedFilterOptionsList({
          selectedFilterOptions,
        }).length > 0 && (
          <GroupedFilterTags
            tags={selectedFilterOptionsList({
              selectedFilterOptions,
            })}
            onTagClick={() => setOpenFilterDrawer(prev => !prev)}
            maxTagsToShow={getMaxTagsToShow({ width })}
          />
        )}
      </div>
      <TableContainer>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby='notificationsTable'
        >
          <TableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            headCells={headState}
            sx={{ textAlign: 'center' }}
          />
          <TableBody>
            {!loadingData ? (
              <>
                {rows.length > 0 ? (
                  <>
                    {visibleRows.map((row, index) => {
                      const labelId = `enhanced-table-checkbox-${index}`

                      return (
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={row.id}
                        >
                          <StickyTableCell
                            component='td'
                            id={labelId}
                            scope='row'
                            padding='none'
                            sx={{ cursor: 'pointer' }}
                            width={120}
                            onClick={() => {
                              setEmployee({
                                id: row.id,
                                name: row.employee,
                              })
                            }}
                          >
                            {row.employee}
                          </StickyTableCell>
                          <CustomTableCell
                            data={row.message}
                            sx={{ textAlign: 'center' }}
                          />
                          <CustomTableCell
                            data={row.date}
                            type='date'
                            width={180}
                            sx={{ textAlign: 'center' }}
                          />
                          <CustomTableCell
                            type="dropdown"
                            data={row.severity}
                            optionType="severity"
                            onChange={newSeverity =>
                              onSeverityChange({
                                id: row.id,
                                severity: newSeverity,
                              })
                            }
                            sx={{ textAlign: 'center' }}
                          />
                          <CustomTableCell
                            type="dropdown"
                            data={row.status}
                            optionType="status"
                            onChange={newStatus =>
                              onStatusChange({
                                id: row.id,
                                status: newStatus,
                              })
                            }
                            sx={{ textAlign: 'center' }}
                          />
                        </TableRow>
                      )
                    })}
                    {emptyRows() > 0 && (
                      <TableRow
                        style={{
                          height: 53 * emptyRows(),
                        }}
                      >
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </>
                ) : (
                  <TableRow style={{ height: 53 * 10 }}>
                    <TableCell
                      colSpan={6}
                      align='center'
                    >
                      {t('no_data')}
                    </TableCell>
                  </TableRow>
                )}
              </>
            ) : (
              <TableRow style={{ height: 53 * 10 }}>
                <TableCell
                  colSpan={6}
                  align='center'
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component='div'
        count={rows.length}
        showFirstButton
        showLastButton
        labelRowsPerPage={''}
        rowsPerPageOptions={[]}
        rowsPerPage={10}
        page={page}
        onPageChange={handleChangePage}
        labelDisplayedRows={({ to, count }) => {t('annotations.tab.displayed_rows', { to: to, count: count })}}
      />
    </>
  )
}

export default ContentTab
